import classNames from 'classnames';
import { Icon, IconName } from 'forms';
import t from 'rcm-i18n';
import React from 'react';
import PortalToBody from 'react-portal-to-body';
import '../../styles/status-overlay.scss';
import { CrudStatus } from '../types';

interface Props {
    bodyError?: string;
    bodyInterrupted?: string;
    bodyLoaded?: string;
    bodyLoading?: string;
    bodySaved?: string;
    bodySaving?: string;
    headingError?: string;
    headingInterrupted?: string;
    headingLoaded?: string;
    headingLoading?: string;
    headingSaved?: string;
    headingSaving?: string;
    status: CrudStatus;
}

/**
 * Indicator for the current status of a CRUD view
 *
 * Display this at all times while the CRUD is open, but change the `status`
 * property whenever the CRUD's status changes.
 */
const StatusOverlay = ({
    bodyError = '',
    bodyInterrupted = '',
    bodyLoaded = '',
    bodyLoading = '',
    bodySaved = '',
    bodySaving = '',
    headingError = `${t('An error occurred')}`,
    headingInterrupted = `${t('Interrupted')}`,
    headingLoaded = `${t('Loaded')}`,
    headingLoading = `${t('Loading')}...`,
    headingSaved = `${t('Changes Saved')}`,
    headingSaving = `${t('Saving')}...`,
    status = 'ready',
}: Props) => <PortalToBody>
    <div className={
        classNames(
            'status-overlay',
            status,
            {
                'has-body': (
                    bodyError ||
                    bodyInterrupted ||
                    bodyLoaded ||
                    bodyLoading ||
                    bodySaved ||
                    bodySaving
                ),
                'resolved': [
                    'saved',
                    'loaded',
                    'error',
                    'interrupted',
                    'ready',
                ].includes(status),
            }
        )
    }>
        {s(status, 'saving', 'cog', headingSaving, bodySaving)}
        {s(status, 'saved', 'ok-sign', headingSaved, bodySaved)}
        {s(status, 'error', 'exclamation-sign', headingError, bodyError)}
        {s(status, 'interrupted', 'stop', headingInterrupted, bodyInterrupted)}
        {s(status, 'loading', 'cog', headingLoading, bodyLoading)}
        {s(status, 'loaded', 'ok-sign', headingLoaded, bodyLoaded)}
    </div>
</PortalToBody>;

const s = (
    statA: CrudStatus,
    statB: CrudStatus,
    icon: IconName,
    heading: string,
    body?: string
) => (
    <div className={`status-content ${statA === statB ? 'active' : ''}`}>
        <div className="status-header">
            <Icon icon={icon} />
            &nbsp;
            <span className="status-header-message" >{heading}</span>
        </div>
        {body && <div className="status-body">
            {body}
        </div>}
    </div>
);

export default StatusOverlay;
